import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlarmClock, CalendarPlus, Check, Mail, MessageCircle, NotebookPen, Phone, Trophy, Video } from 'lucide-react';
import { toast } from 'react-toastify';
import { SnoozeMenu } from './StageKit';
import LogActivityDialog from './LogActivityDialog';
import { ACTIVITY_TYPES, apiMessage, completeActivity, dueBucket, dueLabel, leadPipelineStages, moveLeadToStage, nextFollowUpForLead, rescheduleActivity, stageTones } from './crmWork';
import './lead-focus.css';

// AUTO-CRM-32: the top of a lead: where it is in the pipeline, and what happens next.
const TYPE_ICONS = { CALL: Phone, WHATSAPP: MessageCircle, MEETING: Video, EMAIL: Mail, FOLLOWUP: AlarmClock, NOTE: NotebookPen };
const changed = () => { window.dispatchEvent(new Event('crm:followups-changed')); window.dispatchEvent(new Event('leads-reset')); };

export function StageStepper({ lead, onMoved }) {
  const [stages, setStages] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let active = true;
    if (lead?.id) leadPipelineStages(lead.id).then((rows) => { if (active) setStages(rows); }).catch(() => { if (active) setStages([]); });
    return () => { active = false; };
  }, [lead?.id]);
  const tones = useMemo(() => stageTones(stages || []), [stages]);
  const currentId = String(lead?.lead_stage_id ?? lead?.stage?.id ?? '');
  const open = (stages || []).filter((stage) => !stage.is_closed);
  const closed = (stages || []).filter((stage) => stage.is_closed);
  const currentIndex = open.findIndex((stage) => String(stage.id) === currentId);

  const move = async (stage) => {
    setBusy(true);
    try {
      await moveLeadToStage(lead.id, stage.id);
      toast.success(tones[stage.id]?.kind === 'won' ? `${lead.name} is won. Nice work!` : `Moved to ${stage.name}`);
      setConfirm(null);
      changed();
      onMoved?.();
    } catch (error) {
      toast.error(apiMessage(error, 'Couldn’t move this lead.'));
    } finally {
      setBusy(false);
    }
  };

  if (!stages || !stages.length) return null;
  return (
    <div className="crm-stepper-wrap">
      <ol className="crm-stepper" aria-label="Pipeline stages">
        {open.map((stage, index) => {
          const state = String(stage.id) === currentId ? 'now' : currentIndex >= 0 && index < currentIndex ? 'done' : 'next';
          return (
            <li key={stage.id} className={`is-${state}`} style={{ '--tone': tones[stage.id]?.color }}>
              <button type="button" onClick={() => state !== 'now' && setConfirm(stage)} aria-current={state === 'now' ? 'step' : undefined} disabled={busy} title={state === 'now' ? `${lead.name} is here` : `Move to ${stage.name}`}>
                <span className="crm-stepper-dot" aria-hidden="true">{state === 'done' ? <Check size={10} strokeWidth={3} /> : null}</span>
                <span className="crm-stepper-name">{stage.name}</span>
              </button>
            </li>
          );
        })}
      </ol>
      {closed.length > 0 && (
        <div className="crm-stepper-outcomes" role="group" aria-label="Finish">
          {closed.map((stage) => {
            const tone = tones[stage.id];
            const now = String(stage.id) === currentId;
            return (
              <button key={stage.id} type="button" className={`crm-outcome is-${tone.kind}${now ? ' is-now' : ''}`} disabled={busy || now} onClick={() => setConfirm(stage)} aria-pressed={now}>
                {tone.kind === 'won' && <Trophy size={13} aria-hidden="true" />}{now ? stage.name : `Mark ${stage.name.toLowerCase()}`}
              </button>
            );
          })}
        </div>
      )}
      {confirm && (
        <div className="crm-stepper-confirm" role="alertdialog" aria-label={`Move to ${confirm.name}?`}>
          <span className="ws-grow">Move <b>{lead.name}</b> to <b>{confirm.name}</b>?{confirm.is_closed ? ' This closes the lead.' : ' The lead starts that stage’s first step.'}</span>
          <button type="button" className="ws-btn ws-btn--quiet ws-btn--sm" onClick={() => setConfirm(null)} disabled={busy}>Cancel</button>
          <button type="button" className="ws-btn ws-btn--primary ws-btn--sm" onClick={() => move(confirm)} disabled={busy}>{busy ? 'Moving…' : 'Move'}</button>
        </div>
      )}
    </div>
  );
}

export function NextStepBar({ lead, onChanged }) {
  const [next, setNext] = useState(undefined);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    if (!lead?.id) return;
    nextFollowUpForLead(lead.id).then(setNext).catch(() => setNext(null));
  }, [lead?.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    window.addEventListener('crm:followups-changed', load);
    return () => window.removeEventListener('crm:followups-changed', load);
  }, [load]);

  const done = async () => {
    setBusy(true);
    try { await completeActivity(next.id); toast.success('Done. Plan what’s next?'); changed(); setDialog('plan'); onChanged?.(); }
    catch (error) { toast.error(apiMessage(error, 'Couldn’t mark this as done.')); }
    finally { setBusy(false); }
  };
  const snooze = async (when) => {
    setBusy(true);
    try { await rescheduleActivity(next.id, when); toast.info(`Moved to ${dueLabel(when)}`); changed(); onChanged?.(); }
    catch (error) { toast.error(apiMessage(error, 'Couldn’t move this follow-up.')); }
    finally { setBusy(false); }
  };

  const Icon = next ? TYPE_ICONS[next.type] || AlarmClock : CalendarPlus;
  const late = next && dueBucket(next.scheduled_at) === 'overdue';
  const closed = Boolean(lead?.stage?.is_closed);
  return (
    <>
      <div className={`crm-nextbar${late ? ' is-late' : ''}${!next && next !== undefined ? ' is-empty' : ''}`}>
        <span className="crm-nextbar-icon" aria-hidden="true"><Icon size={17} /></span>
        <div className="ws-grow crm-nextbar-copy">
          {next === undefined ? <span className="ws-muted">Checking the next step…</span>
            : next ? (
              <>
                <span className="crm-nextbar-over">{late ? 'Overdue' : 'Next step'} · {dueLabel(next.scheduled_at)}</span>
                <span className="crm-nextbar-title">{ACTIVITY_TYPES[next.type]?.verb || 'Follow up with'} {String(lead.name).split(/\s+/)[0]}{next.note ? ` · ${next.note}` : ''}</span>
              </>
            ) : (
              <>
                <span className="crm-nextbar-over">{closed ? 'Closed lead' : 'No next step'}</span>
                <span className="crm-nextbar-title">{closed ? 'Nothing planned. Plan a check-in if you want to stay in touch.' : 'Nothing planned. Plan a follow-up so this lead doesn’t go cold.'}</span>
              </>
            )}
        </div>
        <div className="crm-nextbar-actions">
          {next && <SnoozeMenu onPick={snooze} disabled={busy} />}
          {next && <button type="button" className="ws-btn ws-btn--quiet ws-btn--sm" onClick={done} disabled={busy}><Check size={13} />Done</button>}
          {!next && next !== undefined && <button type="button" className="ws-btn ws-btn--quiet ws-btn--sm" onClick={() => setDialog('plan')}><CalendarPlus size={13} />Plan follow-up</button>}
          <button type="button" className="ws-btn ws-btn--primary ws-btn--sm" onClick={() => setDialog('log')}><NotebookPen size={13} />Log what happened</button>
        </div>
      </div>
      {dialog && <LogActivityDialog lead={lead} mode={dialog} pendingFollowUp={dialog === 'log' ? next : null} onClose={() => setDialog(null)} onSaved={() => { load(); changed(); onChanged?.(); }} />}
    </>
  );
}
